import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import ratios from 'Components/utils/ratios/ratios.utils';
import { t } from 'Src/i18n';
import { LabeledIconsList } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import { Image } from 'Components/frameworks/image/Image';
import { Icon } from 'Components/frameworks/icon/Icon';
import type {
  ComparatorColumn,
  ComparatorAvailabilityBoard,
  ComparatorAllAvailableItems,
} from './ProductRoomsComparator.utils';
import { ITEMS_ORDER, SECTION_LABELS, sortCategoriesItems } from './ProductRoomsComparator.utils';
type Props = {
  allCategoriesItems: ComparatorAllAvailableItems;
  availabilityBoards: Array<ComparatorAvailabilityBoard>;
  columns: Array<ComparatorColumn>;
};
export const ProductRoomsComparatorLarge = ({
  allCategoriesItems,
  availabilityBoards,
  columns,
}: Props) => {
  const {
    thTitle,
    thInfoTitle,
    thInfo,
    thStrength,
    thSectionHeader,
    thSectionTitle,
    thSectionHeaderItem,
    thIcon,
    thTableCell,
    thCheck,
  } = {
    thTitle: 'font-brand text-20 text-black text-center',
    thInfoTitle: 'text-mediumGrey flex gap-15 items-center py-10',
    thInfo: 'font-bold text-16 flex items-center justify-center',
    thStrength: 'py-10 flex items-center justify-center',
    thSectionHeader: 'flex mb-20',
    thSectionTitle: 'font-bold text-22 text-black',
    thSectionHeaderItem: 'flex items-center justify-center',
    thIcon: 'text-black me-15 shrink-0',
    thTableCell: 'px-10',
    thCheck: 'flex items-center justify-center',
  };
  const columnWidth = `${100 / (columns.length + 1)}%`;

  const getHorizontalAvailabilityLinesFromItemType = (itemType) =>
    allCategoriesItems[ITEMS_ORDER.indexOf(itemType)]
      .sort(sortCategoriesItems)
      .map((item, index) => {
        return (
          <div className="flex" key={index}>
            <div
              className="flex items-center py-10 text-16"
              style={{
                width: columnWidth,
              }}
            >
              <Icon
                alt=""
                className={thIcon}
                name={ICONS[item.id] || ICONS.TRIDENT1}
                width="1.25rem"
              />
              {item.label}
            </div>
            {availabilityBoards.map((board, boardIndex) => {
              return (
                <div
                  key={boardIndex}
                  className={classnames(thTableCell, thCheck)}
                  style={{
                    width: columnWidth,
                  }}
                >
                  <Icon
                    className={board[itemType][index] ? 'text-green' : 'text-grey'}
                    name={board[itemType][index] ? ICONS.CHECK : ICONS.CROSS}
                    width="1rem"
                  />
                </div>
              );
            })}
          </div>
        );
      });

  return (
    <div className="mx-auto max-w-site px-20 pt-60">
      <div className="flex justify-end">
        {columns.map((column, index) => (
          <div
            className={classnames('text-center', thTableCell)}
            key={index}
            style={{
              width: columnWidth,
            }}
          >
            <div className="mb-30 max-h-30">
              <div className={thTitle}>{column.title}</div>
              {column.price && <div className="font-sans font-bold text-black">{column.price}</div>}
            </div>
            <div className="overflow-hidden rounded-12">
              <Image
                altMsg={column.title}
                hasLazyLoading
                imageUrl={column.picture?.url}
                ratioKey={ratios.XL}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex">
        <div
          className={thInfoTitle}
          style={{
            width: columnWidth,
          }}
        >
          <Icon className="text-black" name={ICONS.SINGLE_ROOM} width="1.875rem" />
          {t('common.accommodations.size')}
        </div>
        {columns.map((column, index) => (
          <div
            key={index}
            className={classnames(thTableCell, thInfo)}
            style={{
              width: columnWidth,
            }}
          >
            {column.surface}
          </div>
        ))}
      </div>
      <div className="flex">
        <div
          className={thInfoTitle}
          style={{
            width: columnWidth,
          }}
        >
          <Icon className="text-black" name={ICONS.PEOPLE_DOUBLE} width="1.875rem" />
          {t('common.accommodations.capacity')}
        </div>
        {columns.map((column, index) => (
          <div
            key={index}
            className={classnames(thTableCell, thInfo)}
            style={{
              width: columnWidth,
            }}
          >
            {column.capacity}
          </div>
        ))}
      </div>
      <div className="flex">
        <div
          className={thInfoTitle}
          style={{
            width: columnWidth,
          }}
        >
          {t('common.accommodations.strengths')}
        </div>
        {columns.map((column, index) => (
          <div
            key={index}
            className={classnames(thTableCell, thStrength)}
            style={{
              width: columnWidth,
            }}
          >
            <LabeledIconsList
              items={column.strengths.map((strength) => {
                return {
                  icon: ICONS[strength.id] || ICONS.TRIDENT1,
                  labels: [strength.label],
                };
              })}
              theme="comparator"
            />
          </div>
        ))}
      </div>

      {ITEMS_ORDER.map(
        (itemType) =>
          !!allCategoriesItems[ITEMS_ORDER.indexOf(itemType)].length && (
            <div key={itemType} className="border-t-1 border-lightGrey py-30">
              <div className={thSectionHeader}>
                <div
                  className={thSectionTitle}
                  style={{
                    width: columnWidth,
                  }}
                >
                  {t(SECTION_LABELS[itemType])}
                </div>
                {columns.map((column, index) => (
                  <div
                    key={index}
                    className={classnames(thTitle, thSectionHeaderItem, thTableCell)}
                    style={{
                      width: columnWidth,
                    }}
                  >
                    {column.title}
                  </div>
                ))}
              </div>
              {getHorizontalAvailabilityLinesFromItemType(itemType)}
            </div>
          ),
      )}
    </div>
  );
};
